import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Button, Input, Text, useToast, Progress, Badge,
  useColorModeValue, Stack, FormControl, FormLabel,
  Alert, AlertIcon, Table, Thead, Tbody, Tr, Th, Td,
  Heading, Container, Flex, HStack, VStack, Divider, Select
} from "@chakra-ui/react";
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { getCustomers } from "services/woo";
import { updateAdminUser } from "services/wpUsers";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";

// Roles de "estado" del alumno soportados por el sincronizador, en orden de display
const ROLE_ORDER = ['customer', 'becado', 'cobranza', 'subscriber'];
const ROLE_META = {
  customer:   { label: 'Activo',    color: 'green'  },
  becado:     { label: 'Becado',    color: 'purple' },
  cobranza:   { label: 'Cobranza',  color: 'orange' },
  subscriber: { label: 'Archivado', color: 'gray'   },
};

// Prioridad al resolver RUNs duplicados en el Excel: el estado mas "especial" gana
const ROLE_PRIORITY = { cobranza: 3, becado: 2, customer: 1, subscriber: 0 };

const CUSTOMER_VALUES = new Set([
  'si', 'sí', 'yes', '1', 'true', 'verdadero', 'activo', 'x', 'active', 'v',
  'vigente', 'customer', 'matriculado', 'matricula'
]);
const BECADO_VALUES = new Set(['becado', 'becada', 'beca', 'scholarship']);
const COBRANZA_VALUES = new Set([
  'cobranza', 'cobranza judicial', 'judicial', 'en cobranza', 'demandado', 'demanda'
]);
// Cualquier otro valor reconocido de "no vigente" cae aca; un valor no reconocido
// tambien cae aca por seguridad (si no esta claro, se archiva).
const SUBSCRIBER_VALUES = new Set([
  'no', 'archivado', 'inactivo', 'subscriber', 'retirado', 'desvinculado', 'baja', 'no vigente'
]);

// El RUN en el Excel ya esta en formato correcto (sin puntos, con guion)
// igual al username en WP. Solo se normaliza a lowercase y trim.
const normalizeRun = (run) => {
  if (!run) return '';
  return String(run).trim().toLowerCase();
};

// Interpreta el valor crudo de la columna Estado/Rol y resuelve a uno de los 4 roles.
const parseTargetRole = (val) => {
  const v = String(val ?? '').toLowerCase().trim();
  if (!v) return 'subscriber';
  if (COBRANZA_VALUES.has(v)) return 'cobranza';
  if (BECADO_VALUES.has(v)) return 'becado';
  if (CUSTOMER_VALUES.has(v)) return 'customer';
  // Incluye SUBSCRIBER_VALUES explicitos y cualquier valor no reconocido
  return 'subscriber';
};

// Detecta columna prefiriendo coincidencia exacta sobre parcial
const detectColumn = (headers, patterns) => {
  // 1. Buscar coincidencia exacta primero
  const exact = headers.find(h =>
    patterns.some(p => h.toLowerCase().trim() === p.toLowerCase())
  );
  if (exact) return exact;
  // 2. Buscar que empiece con el patron
  const startsWith = headers.find(h =>
    patterns.some(p => h.toLowerCase().trim().startsWith(p.toLowerCase()))
  );
  if (startsWith) return startsWith;
  // 3. Buscar que contenga el patron (mas amplio)
  return headers.find(h =>
    patterns.some(p => h.toLowerCase().trim().includes(p.toLowerCase()))
  ) || '';
};

const emptyByRole = () => ROLE_ORDER.reduce((acc, r) => ({ ...acc, [r]: 0 }), {});

const RoleImporter = () => {
  const [fileName, setFileName] = useState('');
  const [fileHeaders, setFileHeaders] = useState([]);
  const [fileData, setFileData] = useState([]);
  const [isExcel, setIsExcel] = useState(true);
  const [runColumn, setRunColumn] = useState('');
  const [statusColumn, setStatusColumn] = useState('');

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const [analysis, setAnalysis] = useState(null);
  const [changeList, setChangeList] = useState([]);
  const [allWpUsers, setAllWpUsers] = useState([]);
  const [results, setResults] = useState([]);
  const [finalStats, setFinalStats] = useState(null);

  const toast = useToast();
  const bgColor = useColorModeValue("white", "gray.700");

  const getCellValue = useCallback((row, col) => {
    if (!col) return '';
    if (isExcel) {
      const idx = fileHeaders.indexOf(col);
      return idx !== -1 && row[idx] != null ? String(row[idx]).trim() : '';
    }
    return row[col] != null ? String(row[col]).trim() : '';
  }, [fileHeaders, isExcel]);

  // Preview completo de todas las filas del Excel
  const dataPreview = useMemo(() => {
    if (!fileData.length || !runColumn) return null;
    const rows = fileData.map(row => {
      const rawRun = getCellValue(row, runColumn);
      if (!rawRun) return null;
      const rawStatus = getCellValue(row, statusColumn);
      return {
        rawRun,
        nRun: normalizeRun(rawRun),
        rawStatus,
        targetRole: parseTargetRole(rawStatus)
      };
    }).filter(Boolean);

    const totalsByRole = ROLE_ORDER.reduce(
      (acc, r) => ({ ...acc, [r]: rows.filter(row => row.targetRole === r).length }),
      {}
    );
    return { rows, totalValid: rows.length, totalsByRole };
  }, [fileData, runColumn, statusColumn, getCellValue]);

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xls', 'xlsx', 'csv'].includes(ext)) {
      toast({ title: "Formato invalido", description: "Solo Excel o CSV", status: "error", duration: 3000 });
      return;
    }

    setFileName(file.name);
    setAnalysis(null);
    setChangeList([]);
    setAllWpUsers([]);
    setResults([]);
    setFinalStats(null);
    setRunColumn('');
    setStatusColumn('');

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        if (['xls', 'xlsx'].includes(ext)) {
          const wb = XLSX.read(ev.target.result, { type: 'array' });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          if (data.length <= 1) throw new Error('Archivo vacio o sin datos');
          const headers = data[0].map(h => String(h));
          setFileHeaders(headers);
          setFileData(data.slice(1).filter(row => row.some(cell => cell != null && cell !== '')));
          setIsExcel(true);

          // Auto-detectar columnas con logica precisa
          const detectedRun = detectColumn(headers, ['run alumno', 'rut alumno', 'run', 'rut']);
          const detectedStatus = detectColumn(headers, ['estado', 'rol', 'situacion', 'categoria', 'vigente']);
          if (detectedRun) setRunColumn(detectedRun);
          if (detectedStatus) setStatusColumn(detectedStatus);

        } else {
          Papa.parse(ev.target.result, {
            header: true,
            skipEmptyLines: true,
            complete: (result) => {
              const headers = Object.keys(result.data[0] || {});
              setFileHeaders(headers);
              setFileData(result.data);
              setIsExcel(false);

              const detectedRun = detectColumn(headers, ['run alumno', 'rut alumno', 'run', 'rut']);
              const detectedStatus = detectColumn(headers, ['estado', 'rol', 'situacion', 'categoria', 'vigente']);
              if (detectedRun) setRunColumn(detectedRun);
              if (detectedStatus) setStatusColumn(detectedStatus);
            }
          });
        }

        toast({ title: "Archivo cargado", description: "Verifica las columnas detectadas en la vista previa antes de analizar", status: "success", duration: 4000 });
      } catch (err) {
        toast({ title: "Error al leer archivo", description: err.message, status: "error", duration: 3000 });
      }
    };

    if (['xls', 'xlsx'].includes(ext)) reader.readAsArrayBuffer(file);
    else reader.readAsText(file);
    e.target.value = '';
  }, [toast]);

  const buildExcelMap = () => {
    const map = new Map();
    for (const row of fileData) {
      const rawRun = getCellValue(row, runColumn);
      if (!rawRun) continue;
      const nRun = normalizeRun(rawRun);
      if (!nRun) continue;
      const rawStatus = getCellValue(row, statusColumn);
      const role = parseTargetRole(rawStatus);
      // Si el mismo RUN aparece varias veces, el estado con mayor prioridad gana
      // (evita perder un estado especial como cobranza/becado por una fila duplicada mas generica)
      if (!map.has(nRun) || ROLE_PRIORITY[role] > ROLE_PRIORITY[map.get(nRun)]) {
        map.set(nRun, role);
      }
    }
    return map;
  };

  // Carga todos los usuarios WP (customer, becado, cobranza, subscriber) en un solo
  // recorrido paginado sobre /datofy/v1/users, que ya devuelve los 4 roles por defecto.
  const loadAllWPUsers = async () => {
    const allUsers = [];
    const loadedByRole = emptyByRole();
    const PER_PAGE = 100;

    let page = 1;
    while (true) {
      const { data: users } = await getCustomers({ role: '', page, per_page: PER_PAGE });
      if (!users || users.length === 0) break;

      for (const u of users) {
        const role = (u.role || '').split(',')[0].trim();
        allUsers.push({ ...u, currentRole: role });
        if (loadedByRole[role] !== undefined) loadedByRole[role]++;
      }

      if (users.length < PER_PAGE) break;
      page++;
    }

    return { users: allUsers, loadedByRole };
  };

  const analyze = async () => {
    if (!runColumn) {
      toast({ title: "Selecciona la columna de RUN", status: "error", duration: 3000 });
      return;
    }
    if (!statusColumn) {
      toast({ title: "Selecciona la columna de Estado/Rol", status: "error", duration: 3000 });
      return;
    }

    setIsAnalyzing(true);
    setAnalysis(null);
    setChangeList([]);
    setAllWpUsers([]);
    setResults([]);
    setFinalStats(null);

    try {
      const excelMap = buildExcelMap();

      if (excelMap.size === 0) {
        toast({ title: "Excel sin datos validos", description: "No se encontraron RUNs en la columna seleccionada", status: "error", duration: 5000 });
        setIsAnalyzing(false);
        return;
      }

      const { users: wpUsers, loadedByRole } = await loadAllWPUsers();
      setAllWpUsers(wpUsers);

      if (wpUsers.length === 0) {
        toast({ title: "Sin usuarios en WordPress", description: "No se encontraron usuarios con rol customer, becado, cobranza ni subscriber", status: "warning", duration: 5000 });
        setIsAnalyzing(false);
        return;
      }

      // Mapa WP: run normalizado → usuario
      const wpMap = new Map();
      for (const u of wpUsers) {
        const nRun = normalizeRun(u.username);
        if (nRun) wpMap.set(nRun, u);
      }

      const changes = [];
      const keepByRole = emptyByRole();
      let matchedInExcel = 0;
      let skippedNotInExcel = 0;

      // Solo se tocan los RUNs presentes en el Excel — el resto de los usuarios
      // de WordPress se deja tal como esta, sin archivar automaticamente.
      for (const [nRun, wpUser] of wpMap) {
        if (!excelMap.has(nRun)) {
          skippedNotInExcel++;
          continue;
        }
        matchedInExcel++;
        const targetRole = excelMap.get(nRun);

        if (wpUser.currentRole === targetRole) {
          if (keepByRole[targetRole] !== undefined) keepByRole[targetRole]++;
        } else {
          changes.push({
            id: wpUser.id,
            username: wpUser.username,
            name: `${wpUser.first_name || ''} ${wpUser.last_name || ''}`.trim(),
            currentRole: wpUser.currentRole,
            targetRole,
            reason: `Estado en Excel: ${ROLE_META[targetRole]?.label || targetRole}`
          });
        }
      }

      // Cuantos en Excel no tienen usuario WP
      let notInWP = 0;
      for (const nRun of excelMap.keys()) {
        if (!wpMap.has(nRun)) notInWP++;
      }

      const changesByRole = emptyByRole();
      for (const c of changes) changesByRole[c.targetRole]++;

      const excelByRole = emptyByRole();
      for (const role of excelMap.values()) excelByRole[role]++;

      setAnalysis({
        excelCount: excelMap.size,
        excelByRole,
        wpCount: wpUsers.length,
        loadedByRole,
        matchedInExcel,
        skippedNotInExcel,
        keepByRole,
        changesByRole,
        notInWP,
        totalChanges: changes.length
      });
      setChangeList(changes);

    } catch (err) {
      toast({ title: "Error en analisis", description: err.message, status: "error", duration: 5000 });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const executeChanges = async () => {
    if (!changeList.length) return;
    setIsProcessing(true);
    setProgress(0);
    setResults([]);

    const stats = { total: changeList.length, success: 0, errors: 0, byRole: emptyByRole() };
    const BATCH_SIZE = 5;

    try {
      for (let i = 0; i < changeList.length; i += BATCH_SIZE) {
        const batch = changeList.slice(i, i + BATCH_SIZE);

        const batchResults = await Promise.all(
          batch.map(async (item) => {
            try {
              await updateAdminUser(item.id, { role: item.targetRole });
              return {
                username: item.username,
                name: item.name,
                from: item.currentRole,
                to: item.targetRole,
                success: true,
                message: 'OK'
              };
            } catch (err) {
              return {
                username: item.username,
                name: item.name,
                from: item.currentRole,
                to: item.targetRole,
                success: false,
                message: err.response?.data?.message || err.message || 'Error'
              };
            }
          })
        );

        for (const value of batchResults) {
          if (value.success) {
            stats.success++;
            if (stats.byRole[value.to] !== undefined) stats.byRole[value.to]++;
          } else {
            stats.errors++;
          }
          setResults(prev => [...prev, value]);
        }

        setFinalStats({ ...stats });
        setProgress(((stats.success + stats.errors) / stats.total) * 100);

        if (i + BATCH_SIZE < changeList.length) {
          await new Promise(res => setTimeout(res, 500));
        }
      }

      toast({
        title: "Sincronizacion completada",
        description: `${stats.success} exitosos, ${stats.errors} errores`,
        status: stats.errors === 0 ? "success" : "warning",
        duration: 5000
      });
    } catch (err) {
      toast({ title: "Error", description: err.message, status: "error", duration: 4000 });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Container maxW="7xl" py={4}>
      <VStack spacing={6} align="stretch">

        {/* Paso 1: Cargar archivo y mapear columnas */}
        <Card>
          <CardHeader>
            <Heading size="md">1. Cargar Archivo y Verificar Columnas</Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Sube el Excel, verifica que las columnas detectadas sean correctas y revisa la vista previa antes de analizar.
            </Text>
          </CardHeader>
          <CardBody>
            <Stack spacing={4}>
              <FormControl maxW="450px">
                <FormLabel>Archivo Excel/CSV</FormLabel>
                <Input
                  type="file"
                  accept=".xls,.xlsx,.csv"
                  onChange={handleFileUpload}
                  disabled={isProcessing || isAnalyzing}
                />
              </FormControl>

              {fileHeaders.length > 0 && (
                <Stack spacing={4}>
                  <Flex gap={4} align="flex-end" flexWrap="wrap">
                    <FormControl maxW="260px">
                      <FormLabel fontSize="sm">
                        Columna RUN/RUT *{' '}
                        {runColumn && <Badge colorScheme="green" ml={1}>detectada</Badge>}
                      </FormLabel>
                      <Select value={runColumn} onChange={e => setRunColumn(e.target.value)}>
                        <option value="">Seleccionar...</option>
                        {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                      </Select>
                    </FormControl>
                    <FormControl maxW="260px">
                      <FormLabel fontSize="sm">
                        Columna Estado/Rol *{' '}
                        {statusColumn && <Badge colorScheme="green" ml={1}>detectada</Badge>}
                      </FormLabel>
                      <Select value={statusColumn} onChange={e => setStatusColumn(e.target.value)}>
                        <option value="">Seleccionar...</option>
                        {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                      </Select>
                    </FormControl>
                  </Flex>

                  {/* Vista previa en tiempo real */}
                  {dataPreview && (
                    <Box>
                      <HStack spacing={3} mb={3} flexWrap="wrap">
                        <Badge colorScheme="gray" p={2} borderRadius="md">
                          Total con RUN valido: {dataPreview.totalValid}
                        </Badge>
                        {ROLE_ORDER.map(r => (
                          <Badge key={r} colorScheme={ROLE_META[r].color} p={2} borderRadius="md">
                            {ROLE_META[r].label}: {dataPreview.totalsByRole[r]}
                          </Badge>
                        ))}
                        {!statusColumn && (
                          <Badge colorScheme="red" p={2} borderRadius="md">
                            Selecciona columna Estado/Rol para ver el desglose
                          </Badge>
                        )}
                      </HStack>

                      <Text fontSize="sm" fontWeight="semibold" mb={2}>
                        Vista previa — {dataPreview.rows.length} filas con RUN (col: <strong>{runColumn}</strong>, estado: <strong>{statusColumn || '—'}</strong>):
                      </Text>
                      <Box overflowX="auto" maxH="400px" overflowY="auto">
                        <Table size="sm">
                          <Thead position="sticky" top={0} bg={bgColor} zIndex={1}>
                            <Tr>
                              <Th>#</Th>
                              <Th>RUN en Excel</Th>
                              <Th>Estado en Excel</Th>
                              <Th>Rol a asignar</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {dataPreview.rows.map((r, i) => (
                              <Tr key={i} _hover={{ bg: "gray.50" }}>
                                <Td color="gray.400" fontSize="xs">{i + 1}</Td>
                                <Td fontFamily="mono" fontSize="sm">{r.rawRun}</Td>
                                <Td fontFamily="mono" fontSize="sm">{r.rawStatus || <Text as="span" color="gray.400">—</Text>}</Td>
                                <Td>
                                  <Badge colorScheme={ROLE_META[r.targetRole].color}>
                                    {r.targetRole}
                                  </Badge>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  )}

                  <Box>
                    <Button
                      colorScheme="blue"
                      onClick={analyze}
                      isLoading={isAnalyzing}
                      loadingText="Cargando usuarios de WP..."
                      isDisabled={!runColumn || !statusColumn || isProcessing}
                    >
                      Analizar y comparar con WordPress
                    </Button>
                    {(!runColumn || !statusColumn) && (
                      <Text fontSize="xs" color="red.500" mt={1}>
                        Debes seleccionar ambas columnas para continuar.
                      </Text>
                    )}
                  </Box>
                </Stack>
              )}

              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Logica de roles (columna Estado/Rol):</Text>
                  <Text><Badge colorScheme="green">Activo</Badge> — Si, Sí, Yes, 1, True, Verdadero, Activo, X, Vigente, Customer, Matriculado</Text>
                  <Text><Badge colorScheme="purple">Becado</Badge> — Becado, Becada, Beca, Scholarship</Text>
                  <Text><Badge colorScheme="orange">Cobranza</Badge> — Cobranza, Cobranza Judicial, Judicial, Demandado, Demanda</Text>
                  <Text><Badge colorScheme="gray">Archivado</Badge> — No, Archivado, Inactivo, Subscriber, Retirado, Desvinculado, Baja</Text>
                  <Text color="gray.500" mt={1}>Un valor no reconocido, en una fila con RUN presente en el Excel, → se archiva (Archivado), por seguridad.</Text>
                  <Text color="gray.500" fontWeight="medium">Solo se modifican los RUNs que aparecen en el Excel — el resto de los usuarios de WordPress no se toca.</Text>
                </VStack>
              </Alert>
            </Stack>
          </CardBody>
        </Card>

        {/* Paso 2: Resultado del analisis */}
        {analysis && (
          <Card>
            <CardHeader>
              <Heading size="md">2. Resultado del Analisis</Heading>
            </CardHeader>
            <CardBody>
              <Stack spacing={5}>
                {/* Diagnostico de datos */}
                <Box p={3} bg="gray.50" borderRadius="md">
                  <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.600">Diagnostico de datos</Text>
                  <VStack align="start" spacing={1} fontSize="sm">
                    <Text>
                      Excel: <strong>{analysis.excelCount}</strong> RUNs validos ({ROLE_ORDER.map(r => `${analysis.excelByRole[r]} ${ROLE_META[r].label.toLowerCase()}`).join(', ')})
                    </Text>
                    <Text>
                      WP cargados: <strong>{analysis.wpCount}</strong> ({ROLE_ORDER.map(r => `${analysis.loadedByRole[r]} ${r}`).join(' + ')})
                    </Text>
                    <Text>RUNs coincidentes Excel↔WP: <strong>{analysis.matchedInExcel}</strong></Text>
                    {analysis.notInWP > 0 && (
                      <Text color="yellow.600">En Excel pero no en WP: <strong>{analysis.notInWP}</strong></Text>
                    )}
                    <Text color="gray.500">
                      Usuarios WP fuera del Excel (no se tocan): <strong>{analysis.skippedNotInExcel}</strong>
                    </Text>
                  </VStack>
                </Box>

                {/* Resumen de cambios */}
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>Ya sincronizados</Text>
                  <HStack wrap="wrap" spacing={3}>
                    {ROLE_ORDER.map(r => (
                      <Box key={`keep-${r}`} p={3} bg={`${ROLE_META[r].color}.50`} borderRadius="md" minW="120px" textAlign="center">
                        <Text fontSize="2xl" fontWeight="bold" color={`${ROLE_META[r].color}.700`}>{analysis.keepByRole[r]}</Text>
                        <Text fontSize="sm" color={`${ROLE_META[r].color}.600`}>Ya son {ROLE_META[r].label.toLowerCase()}</Text>
                      </Box>
                    ))}
                  </HStack>
                </Box>

                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>Cambiaran de rol</Text>
                  <HStack wrap="wrap" spacing={3}>
                    {ROLE_ORDER.map(r => (
                      <Box
                        key={`to-${r}`}
                        p={3}
                        bg={analysis.changesByRole[r] > 0 ? `${ROLE_META[r].color}.50` : "gray.50"}
                        borderRadius="md"
                        minW="120px"
                        textAlign="center"
                      >
                        <Text fontSize="2xl" fontWeight="bold" color={analysis.changesByRole[r] > 0 ? `${ROLE_META[r].color}.700` : "gray.400"}>
                          {analysis.changesByRole[r]}
                        </Text>
                        <Text fontSize="sm" color={analysis.changesByRole[r] > 0 ? `${ROLE_META[r].color}.600` : "gray.400"}>
                          Cambiaran a {ROLE_META[r].label.toLowerCase()}
                        </Text>
                      </Box>
                    ))}
                  </HStack>
                </Box>

                {/* Tabla completa de usuarios WP cargados */}
                {allWpUsers.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>
                      Usuarios de WordPress cargados ({allWpUsers.length}):
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      Las filas marcadas "no" en "En Excel" no se modifican — quedan tal como estan.
                    </Text>
                    <Box overflowX="auto" maxH="400px" overflowY="auto">
                      <Table size="sm">
                        <Thead position="sticky" top={0} bg={bgColor} zIndex={1}>
                          <Tr>
                            <Th>#</Th>
                            <Th>Username (RUN en WP)</Th>
                            <Th>Nombre</Th>
                            <Th>Rol actual</Th>
                            <Th>En Excel</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {allWpUsers.map((u, i) => {
                            const nRun = normalizeRun(u.username);
                            const excelRuns = dataPreview ? dataPreview.rows.map(r => r.nRun) : [];
                            const inExcel = excelRuns.includes(nRun);
                            return (
                              <Tr key={i} _hover={{ bg: "gray.50" }} bg={!inExcel ? "orange.50" : undefined}>
                                <Td color="gray.400" fontSize="xs">{i + 1}</Td>
                                <Td fontFamily="mono" fontSize="sm">{u.username}</Td>
                                <Td fontSize="sm">{`${u.first_name || ''} ${u.last_name || ''}`.trim() || '-'}</Td>
                                <Td>
                                  <Badge colorScheme={ROLE_META[u.currentRole]?.color || 'gray'}>
                                    {u.currentRole}
                                  </Badge>
                                </Td>
                                <Td>
                                  <Badge colorScheme={inExcel ? 'green' : 'red'} variant="outline">
                                    {inExcel ? 'si' : 'no'}
                                  </Badge>
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )}

                {analysis.matchedInExcel === 0 && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium">Ningun RUN del Excel coincidio con usuarios de WordPress.</Text>
                      <Text fontSize="sm">Verifica que la columna RUN seleccionada sea la correcta y que el formato de RUN coincida con los nombres de usuario en WP (ej: "12345678-9").</Text>
                    </VStack>
                  </Alert>
                )}

                {changeList.length > 0 ? (
                  <>
                    <Divider />
                    <Flex justify="space-between" align="center">
                      <Heading size="sm">Cambios a ejecutar ({changeList.length})</Heading>
                      <Button
                        colorScheme="orange"
                        onClick={executeChanges}
                        isLoading={isProcessing}
                        loadingText={`Procesando... ${Math.round(progress)}%`}
                        isDisabled={isProcessing}
                      >
                        Ejecutar {changeList.length} cambios de rol
                      </Button>
                    </Flex>

                    <Box overflowX="auto" maxH="400px" overflowY="auto">
                      <Table size="sm">
                        <Thead position="sticky" top={0} bg={bgColor} zIndex={1}>
                          <Tr>
                            <Th>RUN</Th>
                            <Th>Nombre</Th>
                            <Th>Rol actual</Th>
                            <Th>Nuevo rol</Th>
                            <Th>Motivo</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {changeList.map((item, i) => (
                            <Tr key={i} _hover={{ bg: "gray.50" }}>
                              <Td fontWeight="medium" fontFamily="mono">{item.username}</Td>
                              <Td>{item.name || '-'}</Td>
                              <Td>
                                <Badge colorScheme={ROLE_META[item.currentRole]?.color || 'gray'}>
                                  {item.currentRole}
                                </Badge>
                              </Td>
                              <Td>
                                <Badge colorScheme={ROLE_META[item.targetRole].color}>
                                  {item.targetRole}
                                </Badge>
                              </Td>
                              <Td fontSize="xs" color="gray.500">{item.reason}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </>
                ) : (
                  <Alert status={analysis.matchedInExcel === 0 ? "warning" : "success"} borderRadius="md">
                    <AlertIcon />
                    {analysis.matchedInExcel === 0
                      ? "No se encontraron coincidencias. Revisa el diagnostico de datos arriba."
                      : "Todos los roles ya estan sincronizados correctamente."
                    }
                  </Alert>
                )}
              </Stack>
            </CardBody>
          </Card>
        )}

        {/* Progreso de ejecucion */}
        {isProcessing && (
          <Box>
            <Progress value={progress} colorScheme="orange" size="lg" borderRadius="md" />
            <HStack justify="space-between" mt={2}>
              <Text fontSize="sm">
                Procesados: {(finalStats?.success || 0) + (finalStats?.errors || 0)} / {finalStats?.total || 0}
              </Text>
              <Text fontSize="sm" fontWeight="medium">{Math.round(progress)}%</Text>
            </HStack>
          </Box>
        )}

        {/* Resultados finales */}
        {finalStats && (
          <Card>
            <CardHeader>
              <Heading size="md">Resultado de la Sincronizacion</Heading>
            </CardHeader>
            <CardBody>
              <Stack spacing={4}>
                <HStack wrap="wrap" spacing={3}>
                  <Badge colorScheme="blue" variant="solid" p={2} borderRadius="md">Total: {finalStats.total}</Badge>
                  <Badge colorScheme="green" variant="solid" p={2} borderRadius="md">Exitosos: {finalStats.success}</Badge>
                  {finalStats.errors > 0 && (
                    <Badge colorScheme="red" variant="solid" p={2} borderRadius="md">Errores: {finalStats.errors}</Badge>
                  )}
                  {ROLE_ORDER.map(r => finalStats.byRole[r] > 0 && (
                    <Badge key={r} colorScheme={ROLE_META[r].color} variant="solid" p={2} borderRadius="md">
                      {ROLE_META[r].label}: {finalStats.byRole[r]}
                    </Badge>
                  ))}
                </HStack>

                <Box overflowX="auto" maxH="450px" overflowY="auto">
                  <Table size="sm">
                    <Thead position="sticky" top={0} bg={bgColor} zIndex={1}>
                      <Tr>
                        <Th>RUN</Th>
                        <Th>Nombre</Th>
                        <Th>Cambio</Th>
                        <Th>Estado</Th>
                        <Th>Mensaje</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {results.map((r, i) => (
                        <Tr key={i} _hover={{ bg: "gray.50" }}>
                          <Td fontWeight="medium" fontFamily="mono">{r.username}</Td>
                          <Td>{r.name || '-'}</Td>
                          <Td>
                            {r.from && r.to && (
                              <HStack spacing={1}>
                                <Badge colorScheme={ROLE_META[r.from]?.color || 'gray'} variant="outline">{r.from}</Badge>
                                <Text fontSize="xs">→</Text>
                                <Badge colorScheme={ROLE_META[r.to]?.color || 'gray'}>{r.to}</Badge>
                              </HStack>
                            )}
                          </Td>
                          <Td>
                            <Badge colorScheme={r.success ? 'green' : 'red'}>
                              {r.success ? 'Exito' : 'Error'}
                            </Badge>
                          </Td>
                          <Td fontSize="xs" color={r.success ? 'gray.500' : 'red.500'}>{r.message || 'OK'}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Stack>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Container>
  );
};

export default RoleImporter;
